import _ from 'lodash'
import clx from 'classnames'
import { useState, useEffect, useCallback } from 'react'
import moment from 'moment'
import { Loader, Toast, Tip } from '@vzmi/shp_shared_compo'
import { httpHandler } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import styles from './CustomerInterest.css'
import safeAwait from '../../../../../lib/safeAwait'
import SPBaseModule from '../../../../lib/SPBaseModule'
import Tabs from '../../../Common/NewDashboard/Tabs/Tabs'
import TableAction from '../../../Common/NewDashboard/Table/TableAction/TableAction'
import Sort from '../../../Common/NewDashboard/Sort/Sort'
import SummaryDate from './SummaryDate/SummaryDate'
import RankingTable from './RankingTable/RankingTable'
import { SORT_OPTIONS, SORT_MAP, FIELD, FILTER_NOTE } from './constants'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './CustomerInterest.ga'
import { Error } from '../../../Common/StatePage'
import errorHelper from '../../../../../lib/errorHelper'
import { getCouponImage, getListingImage } from '../../OperatingDashboard/Shared'
import withSharedModalsActions from './withSharedModalsActions'
import { SUB_ANCHOR_MAP, CUSTOMER_INTEREST_SUB_ITEMS } from '../../constants'

export const RANKING_SIZE = 5

export const COMPONENT_KEY = 'customer-interest'

export const getListingCostData = (originalListing) => {
  const {
    cost: listingCost,
    models = []
  } = originalListing

  const newCost = +_.get(
    models,
    '0.items[0].cost',
    _.get(models, '0.cost', listingCost)
  )
  return newCost
}

export const validateCouponDisplay = (abandonedCart, originalListing, quantityField) => {
  // 取得目前時間和明天的時間
  const now = moment()
  const tomorrow = moment().add(1, 'day')

  // 檢查數量是否不為 0
  const quantity = _.get(abandonedCart, quantityField, 0)
  const hasValidQuantity = quantity !== 0

  // 檢查開始時間是否有效
  const isStartTimeValid = _.has(originalListing, 'startTs') && moment(originalListing.startTs).isSameOrBefore(now)

  // 檢查結束時間是否有效
  const isEndTimeValid = _.has(originalListing, 'endTs') && moment(originalListing.endTs).isSameOrAfter(tomorrow)

  // 檢查狀態是否有效
  const isStatusValid = _.has(originalListing, 'status') && ['preImpression', 'normal'].includes(originalListing.status)

  // 檢查是否有優惠券折扣
  const hasCouponDiscount = _.has(originalListing, 'allowedDiscounts') && _.includes(originalListing.allowedDiscounts, 'coupon')

  // 檢查是否沒有 swCode 屬性
  const doesNotHaveSwCode = !_.has(originalListing, 'swCode')

  // 檢查是否符合顯示優惠券的條件
  const shouldDisplayCoupon = hasValidQuantity &&
    isStartTimeValid &&
    isEndTimeValid &&
    doesNotHaveSwCode &&
    isStatusValid &&
    hasCouponDiscount

  return shouldDisplayCoupon
}

export const transformToTableData = (statsType, statsData, listings, originalListings) => {
  switch (statsType) {
    case SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link: {
      const listingMap = _.reduce(listings, (map, listing) => {
        const listingId = _.get(listing, 'id', '')
        map[listingId] = listing
        return map
      }, {})
      const data = _.map(statsData, (abandonedCart) => {
        const listingId = _.get(abandonedCart, 'listingId', '')
        const originalListing = _.get(originalListings, listingId, {})
        const listing = _.get(listingMap, listingId, {})
        const {
          name: listingName,
          ...restListing
        } = _.pick(listing, ['name', 'availableCount', 'sku', 'skuSubStationId'])
        const imageUrl = getListingImage(originalListing)
        const couponImage = getCouponImage(originalListing)
        const newCost = getListingCostData(originalListing)
        const item = {
          ...abandonedCart,
          ...(
            _.isEmpty(listing)
              ? { listingName: '賣場已下架' }
              : { listingName, ...restListing }
          ),
          itemPrice: +abandonedCart.itemPrice,
          amount: +abandonedCart.amount,
          image: imageUrl,
          couponImage,
          cost: newCost,
          showAbandonedCartIcon: validateCouponDisplay(abandonedCart, originalListing, FIELD.QUANTITY),
          showYesterdayAbandonedCartIcon: validateCouponDisplay(abandonedCart, originalListing, FIELD.YESTERDAY_QUANTITY),
          listingEndTs: originalListing.endTs,
          couponPrice: +originalListing.price,
          shipType: _.get(listing, 'skuDetail.shipType', {})
        }
        return item
      })
      return data
    }

    case SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ARRIVALNOTICE.link:
      return _.map(statsData, item => {
        return {
          ...item,
          quantity: item.notifyQuantity || item.tagQuantity,
          itemPrice: +(item.itemPrice || item.price),
          amount: +(item.amount || 0),
          availableCount: item.availableQuantity,
          shipType: item.shipType
        }
      })
    case SUB_ANCHOR_MAP.CUSTOMER_INTEREST_TRACKITEM.link:
      return _.map(statsData, item => {
        const listingId = _.get(item, 'listingId', '')
        const originalListing = _.get(originalListings, listingId, {})
        const imageUrl = getListingImage(originalListing)
        const couponImage = getCouponImage(originalListing)
        const newCost = getListingCostData(originalListing)

        return {
          ...item,
          quantity: item.notifyQuantity || item.tagQuantity,
          itemPrice: +(item.itemPrice || item.price),
          amount: +(item.amount || 0),
          image: imageUrl,
          cost: newCost,
          couponImage,
          couponPrice: +originalListing.price,
          availableCount: item.availableQuantity,
          shipType: item.shipType,
          yesterdayQuantity: item.abandonedCartQuantity,
          showAbandonedCartQuantityIcon: validateCouponDisplay(item, originalListing, FIELD.ABANDONED_CART_QUANTITY)
        }
      })
  }
}

export const getListingsData = async (listingId, req, params, resObjName = 'listings') => {
  const reqParams = params || {
    id: listingId,
    limit: 50,
    offset: 0,
    page: 1,
    fields: ''
  }

  const [reqError, payload = []] = await safeAwait(req(reqParams))
  const errorMessage = errorHelper.parseErrorMessage(reqError)
  if (errorMessage) {
    return [{ message: errorMessage, reqParams }]
  }
  const resObj = _.get(payload, resObjName, [])
  return [undefined, resObj]
}
class CustomerInterest extends SPBaseModule {
  constructor (props) {
    super(props)
    this.state = {
      selectedTab: ''
    }
  }

  componentDidMount () {
    this.setState({ selectedTab: SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link })
  }

  componentDidUpdate (prevProps, prevState) {
    const hash = _.get(this.props, 'location.hash', '')
    const subAncherType = _.replace(hash, '#', '')
    if (_.isEqual(subAncherType, prevState.selectedTab)) {
      return
    }
    // 判斷是否為買家有興趣子選單
    const isTabAnchor = _.some(SUB_ANCHOR_MAP, { link: subAncherType })
    if (!isTabAnchor) {
      return
    }
    this.setState({ selectedTab: subAncherType })
  }

  onTabChange = tabKey => {
    const { history } = this.props
    this.setState({ selectedTab: tabKey })
    history.push(`#${tabKey}`)
  }

  sendPageviewByAction = (action) => {
    const i13nModel = getI13nModel(action)
    this.props.sendDashboardPageview(i13nModel)
  }

  onTabClick = (tabKey, e) => {
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TAB,
      payload: tabKey,
      statsType: tabKey
    })
  }

  render () {
    const {
      className,
      sharedLink,
      sendDashboardPageview,
      fetchAbandonedCarts,
      fetchArrivalNoticeSummaries,
      fetchListingTags,
      fetchListings,
      createSupplierCoupons,
      onOpenECouponModal,
      setECouponVisible,
      flags,
      page
    } = this.props
    const { selectedTab } = this.state
    const clz = clx(styles.StatsContainer, {
      [className]: !!className
    })

    const dataFetchFunctions = {
      [SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link]: fetchAbandonedCarts,
      [SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ARRIVALNOTICE.link]: fetchArrivalNoticeSummaries,
      [SUB_ANCHOR_MAP.CUSTOMER_INTEREST_TRACKITEM.link]: fetchListingTags
    }

    const tabs = _.map(CUSTOMER_INTEREST_SUB_ITEMS, (item) => {
      const fetchDataFunction = dataFetchFunctions[item.link]
      return {
        label: item.linkTitle,
        key: item.link,
        children: (
          <StatsContainer
            sharedLink={sharedLink}
            sendDashboardPageview={sendDashboardPageview}
            fetchData={fetchDataFunction}
            statsType={item.link}
            fetchListings={fetchListings}
            createSupplierCoupons={createSupplierCoupons}
            onOpenECouponModal={onOpenECouponModal}
            setECouponVisible={setECouponVisible}
            flags={flags}
            page={page}
          />
        )
      }
    })

    return (
      <div
        className={clz}
        data-role={COMPONENT_KEY}
      >
        <Tabs
          key='tabs'
          id={selectedTab}
          activeKey={selectedTab}
          onChange={this.onTabChange}
          onTabClick={this.onTabClick}
          items={tabs}
        />
      </div>
    )
  }
}

const StatsContainer = ({ sharedLink, sendDashboardPageview, fetchData, statsType, fetchListings, onOpenECouponModal, setECouponVisible, flags, page }) => {
  const [sortType, setSortType] = useState(SORT_OPTIONS[statsType][0].value)
  const [data, setData] = useState([])
  const [reportDate, setReportDate] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const isPM = _.get(page, 'user.identity.isPM', false)

  const onSortChange = e => {
    const newSortType = _.get(e, 'target.value', '')
    if (newSortType === sortType) {
      return
    }

    sendPageviewByAction({
      type: GA_CLICK_ACTION.TABLE_SORT,
      payload: newSortType,
      statsType
    })
    setSortType(newSortType)
  }

  const sendPageviewByAction = (action) => {
    const i13nModel = getI13nModel(action)
    sendDashboardPageview(i13nModel)
  }

  const sendTableLinkPageview = (payload) => {
    sendPageviewByAction({
      type: GA_CLICK_ACTION.TABLE_LINK,
      payload,
      statsType
    })
  }

  const onReadmoreClick = e => {
    sendPageviewByAction({
      type: GA_CLICK_ACTION.READ_MORE,
      statsType
    })
  }

  const fetchListingsInfo = useCallback(async (listingId, targetAudience) => {
    setECouponVisible(true)
    setIsLoading(true)
    await onOpenECouponModal(listingId, targetAudience, data)
    setIsLoading(false)
  }, [data, onOpenECouponModal, setECouponVisible])

  const search = useCallback(async () => {
    const currentSortType = SORT_MAP[statsType][sortType]
    const params = {
      sort: `-${currentSortType}`,
      limit: RANKING_SIZE
    }

    setIsLoading(true)
    const [reqError, payload = {}] = await safeAwait(
      fetchData(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(reqError)
    setIsLoading(false)
    setFetchError(errorMessage)
    if (!_.isEmpty(errorMessage)) {
      return
    }

    const {
      abandonedCarts,
      arrivalNoticeSummaries,
      listingTags,
      listings,
      reportDate
    } = payload

    // 取得賣場資訊
    let originalListings = {}
    if (statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link ||
      statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_TRACKITEM.link) {
      const ids = statsType === SUB_ANCHOR_MAP.CUSTOMER_INTEREST_ABANDONEDCARTS.link
        ? _.map(listings, item => item.id).join(',')
        : _.map(listingTags, item => item.listingId).join(',')
      setIsLoading(true)
      const isEmptyListingIds = _.isEmpty(ids)
      const req = isEmptyListingIds
        ? () => Promise.resolve([])
        : params => fetchListings(params).then(httpHandler)
      const [listingsError, listingsData = []] = await getListingsData(ids, req)
      setIsLoading(false)
      originalListings = _.keyBy(listingsData, 'id')

      if (listingsError) {
        setFetchError(listingsError.message)
        Toast.error(`取得賣場資料失敗：${listingsError.message}`)
        console.error(listingsError.error)
        setIsLoading(false)
        return
      }
    }

    const data = transformToTableData(
      statsType,
      abandonedCarts || arrivalNoticeSummaries || listingTags || [],
      listings || [],
      originalListings
    )

    setData(data)
    setReportDate(reportDate)
  }, [fetchData, sortType, statsType, fetchListings])

  useEffect(() => {
    search()
  }, [sortType, fetchData, statsType, search])

  return (
    <Loader loaded={!isLoading}>
      {
        fetchError
          ? (
            <Error
              onClick={search}
              iconType='update'
              text='系統忙碌中，請稍後再試'
            />
          )
          : (
            <>
              <TableAction
                leftContent={(
                  <SummaryDate
                    date={reportDate}
                    statsType={statsType}
                  />
                )}
              >
                <Tip
                  type='info'
                  className={styles.filterNote}
                >
                  {FILTER_NOTE}
                </Tip>
                <NewFeaturePromo />
                <Sort
                  options={SORT_OPTIONS[statsType]}
                  sortType={sortType}
                  onChange={onSortChange}
                  id={`${statsType}Sort`}
                />
              </TableAction>
              <RankingTable
                data={data}
                sharedLink={sharedLink}
                sendTableLinkPageview={sendTableLinkPageview}
                readmoreProps={{
                  href: `/item/${statsType}`,
                  onClick: onReadmoreClick
                }}
                statsType={statsType}
                fetchListingsInfo={fetchListingsInfo}
                flags={flags}
                isPM={isPM}
              />
            </>
          )
      }
    </Loader>
  )
}

export const NewFeaturePromo = () => {
  return (
    <a
      href='/batchCoupon/dispatch'
      target='_blank'
      rel='noopener noreferrer'
      className={styles.NewFeaturePromo}
    >
      NEW! 批次發送折價券
    </a>
  )
}

export default withSharedModalsActions(CustomerInterest)
